/* istanbul ignore file */

import { useCallback } from 'react';

import { useClient } from 'urql';

import { useOrders } from '@order/features/orders';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { type LineItem, type Order } from '../../utils';
import {
  InStoreOrdersDocument,
  type InStoreOrdersQuery,
  type InStoreOrdersQueryVariables,
} from './GraphQL/InStoreOrders.generated';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A hook for fetching the user's most recent orders with pagination support
 * and line item reorder availability checking.
 */
export const useInStoreOrders = () => {
  const client = useClient();
  const isLoggedIn = useIsLoggedIn();

  // ─── Queries ─────────────────────────────────────────────────────────

  const ordersQuery = useCallback(
    async (input: InStoreOrdersQueryVariables['input']) => {
      const queryOrders = client.query<InStoreOrdersQuery, InStoreOrdersQueryVariables>;

      return queryOrders(
        InStoreOrdersDocument,
        { input },
        { requestPolicy: 'network-only' },
      ).toPromise();
    },
    [client.query],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const fetchOrders = useCallback<FetchOrders>(
    async (context) => {
      const { page, ordersPerPage } = context;

      if (!isLoggedIn) return { orders: [], lastPage: page };

      const { data } = await ordersQuery({ take: ordersPerPage, page });

      const ordersData = data?.inStoreLoyaltyOrders;
      const hasResolvedOrders = ordersData?.__typename === 'InStoreOrdersResponse';

      if (!hasResolvedOrders) {
        return {
          orders: [],
          lastPage: page,
          includeCanceledOrders: true,
          includePlacedOrders: true,
        };
      }

      // NOTE: BE pagination utilizes a zero-based counter (e.g., 0 == 1, 1 == 2, etc.),
      //       therefore, we increase the number by one to match the state machine model.
      const lastPage = (ordersData.pagination.last ?? 0) + 1;

      return { orders: ordersData.inStoreOrders.map(mapToOrder), lastPage };
    },
    [isLoggedIn, ordersQuery],
  );

  // ─────────────────────────────────────────────────────────────────────

  return useOrders<Order>({ fetchOrders });
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const mapToOrder = (order: InStoreOrder): Order => {
  return {
    id: order.id,
    wantedTime: order.wantedTime,
    restaurant: {
      id: order.restaurant.entityId,
      name: order.restaurant.name,
      slug: '', // not needed for in-store orders.
    },
    lineItems: order.lineItems.map(mapToLineItem),
  };
};

const mapToLineItem = (lineItem: InStoreOrder['lineItems'][number]): LineItem => {
  return {
    id: lineItem.brinkId,
    isCustom: lineItem.isCustom,
    product: {
      id: lineItem.brinkId,
      name: lineItem.name,
      isCustom: lineItem.isCustomProduct,
      asset: {
        url: lineItem.asset.url,
      },
    },
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type FetchOrders = UseOrdersParams['fetchOrders'];

type UseOrdersParams = Parameters<typeof useOrders<Order>>[0];

type InStoreOrder = InStoreOrdersQuery['inStoreLoyaltyOrders']['inStoreOrders'][number];
